/**
 * The layout being edited, with undo and redo. Saving it to the server, and keeping unsaved changes on
 * the device, is state/saving.ts.
 */

import { newLayout, readLayout, type Layout, type Placement } from '@mawkeb/shared/layout';
import { create } from 'zustand';

const UNDO_LIMIT = 100;
/** Where milestone 2 kept the one layout a device had, before layouts were saved on the server. */
const key = (hall: string) => `venue.layout.${hall}`;

/** The layout this device kept before saving existed, if it has anything in it. */
export function deviceLayout(hall: string): Layout | null {
  try {
    const raw = localStorage.getItem(key(hall));
    const layout = raw ? readLayout(JSON.parse(raw)) : null;
    return layout && layout.placements.length ? layout : null;
  } catch {
    return null;
  }
}

export function forgetDeviceLayout(hall: string) {
  try {
    localStorage.removeItem(key(hall));
  } catch {
    /* private mode */
  }
}

export interface LayoutState {
  layout: Layout;
  /** What the hall shows while a drag is under way; becomes an undo step when the drag ends. */
  preview: Layout | null;
  selection: string[];
  past: Layout[];
  future: Layout[];
  /** A library item waiting for a tap on the floor (or a drop) to place it. */
  placing: string | null;

  /** Starts editing a layout afresh: no history, nothing selected. */
  reset: (layout: Layout) => void;
  commit: (next: Layout) => void;
  setPreview: (next: Layout | null) => void;
  undo: () => void;
  redo: () => void;
  select: (ids: string[]) => void;
  setPlacing: (item: string | null) => void;
}

/** Edits keep the placements they don't touch as the same objects, so an edit that changed nothing shows here. */
function unchanged(a: Layout, b: Layout): boolean {
  return a === b || (a.divider === b.divider && a.placements.length === b.placements.length && a.placements.every((p, i) => p === b.placements[i]));
}

export const useLayout = create<LayoutState>()((set, get) => ({
  layout: newLayout('zahraa-hall'),
  preview: null,
  selection: [],
  past: [],
  future: [],
  placing: null,

  reset: (layout) => set({ layout, preview: null, selection: [], past: [], future: [], placing: null }),

  commit: (next) => {
    const { layout, past, selection } = get();
    if (unchanged(next, layout)) {
      set({ preview: null });
      return;
    }
    const ids = new Set(next.placements.map((p) => p.id));
    set({ layout: next, preview: null, past: [...past, layout].slice(-UNDO_LIMIT), future: [], selection: selection.filter((id) => ids.has(id)) });
  },

  setPreview: (next) => set({ preview: next }),

  undo: () => {
    const { layout, past, future } = get();
    const prev = past[past.length - 1];
    if (!prev) return;
    set({ layout: prev, preview: null, past: past.slice(0, -1), future: [layout, ...future], selection: keep(get().selection, prev) });
  },

  redo: () => {
    const { layout, past, future } = get();
    const next = future[0];
    if (!next) return;
    set({ layout: next, preview: null, past: [...past, layout], future: future.slice(1), selection: keep(get().selection, next) });
  },

  select: (ids) => set({ selection: ids }),
  setPlacing: (item) => set({ placing: item }),
}));

function keep(selection: string[], layout: Layout): string[] {
  const ids = new Set(layout.placements.map((p) => p.id));
  return selection.filter((id) => ids.has(id));
}

export const layoutState = useLayout.getState;

const RECENT = 'venue.recent';

/** The library's "Recently used": the last few items placed on this device. */
export function rememberRecent(id: string) {
  try {
    const list = JSON.parse(localStorage.getItem(RECENT) ?? '[]') as string[];
    localStorage.setItem(RECENT, JSON.stringify([id, ...list.filter((x) => x !== id)].slice(0, 6)));
  } catch {
    // private mode: no recent list this visit
  }
}

export function recentItems(): string[] {
  try {
    const list = JSON.parse(localStorage.getItem(RECENT) ?? '[]');
    return Array.isArray(list) ? list.filter((x): x is string => typeof x === 'string') : [];
  } catch {
    return [];
  }
}

/** The layout with some placements changed (the rest are the same objects, so nothing else is rebuilt). */
export function withPlacements(layout: Layout, ids: ReadonlySet<string>, change: (p: Placement) => Placement): Layout {
  return { ...layout, placements: layout.placements.map((p) => (ids.has(p.id) ? change(p) : p)) };
}
